import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "@/lib/api/client";
import { UserInfo, LoginRequest, RegisterRequest } from "@/lib/types/api";
import { getErrorMessage } from "@/lib/utils/errors";

interface AuthContextType {
  user: UserInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Public routes that don't require authentication
const PUBLIC_ROUTES = ["/login"];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = !!user;
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await apiClient.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const login = async (data: LoginRequest) => {
    const response = await apiClient.login(data);
    if (response.success && response.data?.user) {
      setUser(response.data.user);
      
      // Store user info in localStorage for theme initialization
      localStorage.setItem("userInfo", JSON.stringify(response.data.user));
      
      // Apply user's theme preference if available
      if (response.data.user.selectedTheme) {
        const theme = response.data.user.selectedTheme;
        localStorage.setItem("theme", theme);
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(theme);
      }
      
      navigate("/dashboard");
    } else {
      throw new Error(response.message || "Login failed");
    }
  };

  const logout = async () => {
    try {
      await apiClient.logout();
      setUser(null);
      navigate("/login");
    } catch (error) {
      // Even if logout fails on server, clear local state
      setUser(null);
      navigate("/login");
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => user?.roles?.includes(role)) || false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        logout,
        refreshUser,
        hasRole,
        hasAnyRole,
      }}
    >
      {!isInitialized && !isPublicRoute ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
